(() => {
  if (customElements.get('tss-navigation')) return;
  class TssNavigation extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.desktop = this.dataset.surface === 'desktop';
      this.header = this.closest('.section-header');
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-tss-toggle]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setOpen(button, button.getAttribute('aria-expanded') !== 'true');
        button.focus({ preventScroll: true });
      }, { capture: true, signal });
      if (this.desktop) this.connectDesktop(signal);
      else this.connectMobile(signal);
      this.dataset.ready = 'true';
      this.syncHeaderState();
      document.addEventListener('shopify:block:select', event => {
        const block = event.target.closest('.tss-menu-link');
        if (!block || !this.contains(block)) return;
        const panels = [];
        let panel = block.closest('.tss-group-panel,.tss-menu-panel');
        while (panel) {
          panels.unshift(panel);
          panel = panel.parentElement.closest('.tss-group-panel,.tss-menu-panel');
        }
        panels.forEach(item => {
          const button = this.querySelector(`[aria-controls="${CSS.escape(item.id)}"]`);
          if (button) this.setOpen(button, true);
        });
      }, { signal });
    }
    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      this.abort?.abort();
      this.drawerObserver?.disconnect();
      if (this.drawer?.mainDetailsToggle?.open) {
        this.drawer.closeMenuDrawer(new Event('disconnect'), false);
      }
      this.syncHeaderState();
    }
    setOpen(button, open, restoreFocus = false) {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel || !this.contains(panel)) return;
      if (open && this.desktop) this.closeAll(button);
      if (!open && (restoreFocus || panel.contains(document.activeElement))) {
        button.focus({ preventScroll: true });
      }
      button.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      if (button.hasAttribute('data-tss-top-toggle')) {
        button.closest('[data-tss-item]').classList.toggle('is-open', open);
      }
      this.syncHeaderState();
      if (open && this.desktop) this.measurePanel(button);
    }
    closeAll(except) {
      this.querySelectorAll('[data-tss-top-toggle][aria-expanded="true"]').forEach(button => {
        if (button !== except) this.setOpen(button, false);
      });
    }
    syncHeaderState() {
      if (!this.header) return;
      const mobileOpen = innerWidth < 990 && Boolean(this.header.querySelector('header-drawer > details[open]'));
      const desktopOpen = innerWidth >= 990 && Boolean(this.header.querySelector('tss-navigation[data-surface="desktop"] [data-tss-top-toggle][aria-expanded="true"]'));
      this.header.classList.toggle('tss-navigation-open', this.header.isConnected && (mobileOpen || desktopOpen));
    }
    measurePanel(button) {
      const wrapper = this.closest('.tss-header-wrapper');
      if (!wrapper) return;
      const bottom = wrapper.getBoundingClientRect().bottom;
      const rowBottom = button.parentElement.getBoundingClientRect().bottom;
      this.style.setProperty('--tss-panel-top', `${Math.max(0, bottom)}px`);
      button.closest('[data-tss-item]').style.setProperty('--tss-bridge-height', `${Math.max(0, bottom - rowBottom) + 2}px`);
    }
    connectDesktop(signal) {
      this.querySelectorAll('[data-tss-item]').forEach(item => {
        const button = item.querySelector('[data-tss-top-toggle]');
        if (!button) return;
        item.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
          clearTimeout(this.closeTimer);
          this.setOpen(button, true);
        }, { signal });
        item.addEventListener('pointerleave', event => {
          if (event.pointerType !== 'mouse') return;
          this.closeTimer = setTimeout(() => {
            if (!item.contains(document.activeElement)) this.setOpen(button, false);
          }, 180);
        }, { signal });
        item.addEventListener('focusin', event => {
          clearTimeout(this.closeTimer);
          if (event.target.matches('.tss-top-link')) this.setOpen(button, true);
        }, { signal });
      });
      this.addEventListener('focusin', event => {
        if (event.target.matches('.tss-top-link') && !event.target.closest('[data-tss-item]').querySelector('[data-tss-top-toggle]')) this.closeAll();
      }, { signal });
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          if (this.isConnected && !this.contains(document.activeElement)) this.closeAll();
        }, 0);
      }, { signal });
      this.addEventListener('keydown', event => {
        const item = event.target.closest('[data-tss-item]');
        const button = item?.querySelector('[data-tss-top-toggle]');
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          if (button) this.setOpen(button, false, true);
          else this.closeAll();
        } else if (event.key === 'ArrowDown' && button && event.target.closest('.tss-split-row--top')) {
          event.preventDefault();
          this.setOpen(button, true);
          document.getElementById(button.getAttribute('aria-controls'))?.querySelector('a[href]')?.focus();
        }
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.closeAll();
      }, { signal });
      window.addEventListener('resize', () => { this.closeAll(); this.syncHeaderState(); }, { signal });
      window.addEventListener('scroll', () => this.closeAll(), { passive: true, signal });
    }
    connectMobile(signal) {
      this.drawer = this.closest('header-drawer');
      const details = this.drawer?.querySelector('details');
      const summary = details?.querySelector('summary');
      if (!details || !summary) return;
      this.drawerObserver?.disconnect();
      this.drawerObserver = new MutationObserver(() => this.syncHeaderState());
      this.drawerObserver.observe(details, { attributes: true, attributeFilter: ['open'] });
      this.drawer.addEventListener('keydown', event => {
        if (!details.open) return;
        if (event.key === 'Tab') {
          const focusable = Array.from(details.querySelectorAll('summary,a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'))
            .filter(element => !element.closest('[hidden]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
          if (!focusable.length) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          const current = focusable.indexOf(document.activeElement);
          const next = event.shiftKey ? (current <= 0 ? focusable.length - 1 : current - 1) : (current + 1) % focusable.length;
          focusable[next].focus();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.drawer.closeMenuDrawer(event, summary);
          summary.setAttribute('aria-expanded', 'false');
        }
      }, { capture: true, signal });
      this.drawer.addEventListener('keyup', event => {
        if (event.key === 'Escape') event.stopImmediatePropagation();
      }, { capture: true, signal });
      details.addEventListener('toggle', () => {
        this.syncHeaderState();
        summary.setAttribute('aria-expanded', String(details.open));
        if (!details.open) {
          this.closeAll();
          this.querySelectorAll('[data-default-open]').forEach(button => this.setOpen(button, button.dataset.defaultOpen === 'true'));
          document.body.classList.remove(`overflow-hidden-${this.drawer.dataset.breakpoint}`);
          this.drawer.closest('.section-header')?.classList.remove('menu-open');
        }
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (details.open && !this.drawer.contains(event.target)) {
          this.drawer.closeMenuDrawer(event, false);
          summary.setAttribute('aria-expanded', 'false');
        }
      }, { signal });
      window.addEventListener('resize', event => {
        this.syncHeaderState();
        if (innerWidth >= 990 && details.open) {
          this.drawer.closeMenuDrawer(event, false);
          summary.setAttribute('aria-expanded', 'false');
        } else if (details.open) {
          const header = this.drawer.closest('.section-header');
          document.documentElement.style.setProperty('--viewport-height', `${innerHeight}px`);
          if (header) document.documentElement.style.setProperty('--header-bottom-position', `${header.getBoundingClientRect().bottom}px`);
        }
      }, { signal });
    }
  }
  customElements.define('tss-navigation', TssNavigation);
})();

